'use client';

import * as React from 'react';
import { CheckIcon, ChevronDown } from 'lucide-react';
import flags from 'react-phone-number-input/flags';
import { useTranslation } from 'react-i18next';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { COUNTRIES, type Country } from '@/utils/country';

const PRIORITY_COUNTRIES = ['FR', 'BE', 'CA', 'AE', 'CH'];

const sortedCountries = [
  ...COUNTRIES.filter((c) => PRIORITY_COUNTRIES.includes(c.code)).sort((a, b) => {
    const indexA = PRIORITY_COUNTRIES.indexOf(a.code);
    const indexB = PRIORITY_COUNTRIES.indexOf(b.code);
    return indexA - indexB;
  }),
  ...COUNTRIES.filter((c) => !PRIORITY_COUNTRIES.includes(c.code)).sort((a, b) =>
    a.name.localeCompare(b.name),
  ),
];

const FlagComponent = ({
  countryCode,
  countryName,
}: {
  countryCode: string;
  countryName: string;
}) => {
  const Flag = flags[countryCode as keyof typeof flags];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

type CountrySelectSearchProps = {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  valueFormat?: 'name' | 'code';
};

export const CountrySelectSearch = ({
  value,
  onChange,
  label,
  className,
  disabled,
  valueFormat = 'name',
}: CountrySelectSearchProps) => {
  const { t } = useTranslation('user', { useSuspense: false });
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedCountry = sortedCountries.find((c) =>
    valueFormat === 'code' ? c.code === value : c.name === value,
  );

  const handleSelect = (country: Country) => {
    onChange(valueFormat === 'code' ? country.code : country.name);
    setIsOpen(false);
  };

  const filteredCountries = sortedCountries.filter((country) =>
    country.name.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const priorityCountries = filteredCountries.filter((country) =>
    PRIORITY_COUNTRIES.includes(country.code),
  );
  const otherCountries = filteredCountries.filter(
    (country) => !PRIORITY_COUNTRIES.includes(country.code),
  );

  const showSeparator = priorityCountries.length > 0 && otherCountries.length > 0;

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setSearchValue('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <div
          className={cn(
            'relative px-2 py-1 h-14 rounded-lg flex items-center',
            disabled
              ? 'bg-subtle cursor-not-allowed border-none'
              : 'border border-input bg-white cursor-pointer',
            className,
          )}
          onClick={() => !disabled && setIsOpen(true)}
        >
          {label && selectedCountry && (
            <label
              className={cn(
                'absolute top-0 left-3 px-1 text-sm pointer-events-none',
                disabled ? 'text-disable bg-subtle' : 'text-tertiary bg-white',
              )}
            >
              {label}
            </label>
          )}
          <div
            className={cn(
              'flex-1 flex items-center w-full',
              label && selectedCountry ? 'pt-4' : '',
            )}
          >
            <div
              className={cn(
                'w-full justify-between flex items-center gap-2 h-10 px-2',
                !selectedCountry && 'text-muted-foreground',
                disabled && 'text-disable',
              )}
            >
              <span className="flex items-center gap-2 flex-1 min-w-0">
                {selectedCountry ? (
                  <>
                    <FlagComponent
                      countryCode={selectedCountry.code}
                      countryName={selectedCountry.name}
                    />
                    <span className="truncate">{selectedCountry.name}</span>
                  </>
                ) : (
                  <span className="text-disable">{t('generalInfo.country.label')} </span>
                )}
              </span>
              {!disabled && (
                <ChevronDown
                  className={cn(
                    'ml-2 h-4 text-black size-6 shrink-0 transition-transform duration-200 absolute right-4 top-1/2 -translate-y-1/2',
                    isOpen && 'rotate-180',
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={(val) => {
              setSearchValue(val);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]',
                  );
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder={t('personalData.phoneNumber.searchPlaceholder')}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>{t('personalData.phoneNumber.noCountryFound')}</CommandEmpty>
              {priorityCountries.length > 0 && (
                <CommandGroup>
                  {priorityCountries.map((country) => (
                    <CommandItem
                      key={country.code}
                      value={country.name}
                      onSelect={() => handleSelect(country)}
                      className="gap-2 data-[selected=true]:bg-[#FBFBFB] data-[selected=true]:text-accent-foreground"
                    >
                      <FlagComponent countryCode={country.code} countryName={country.name} />
                      <span className="flex-1 text-sm">{country.name}</span>
                      <CheckIcon
                        className={cn(
                          'ml-auto size-4',
                          (
                            valueFormat === 'code'
                              ? selectedCountry?.code === country.code
                              : selectedCountry?.name === country.name
                          )
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {showSeparator && <div className="border-t border-input my-1" role="separator" />}
              {otherCountries.length > 0 && (
                <CommandGroup>
                  {otherCountries.map((country) => (
                    <CommandItem
                      key={country.code}
                      value={country.name}
                      onSelect={() => handleSelect(country)}
                      className="gap-2 data-[selected=true]:bg-[#FBFBFB] data-[selected=true]:text-accent-foreground"
                    >
                      <FlagComponent countryCode={country.code} countryName={country.name} />
                      <span className="flex-1 text-sm">{country.name}</span>
                      <CheckIcon
                        className={cn(
                          'ml-auto size-4',
                          (
                            valueFormat === 'code'
                              ? selectedCountry?.code === country.code
                              : selectedCountry?.name === country.name
                          )
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
